import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tasksService } from '@/services/tasks.service';
import { clientsService } from '@/services/clients.service';
import type { Task, Client } from '@/types';
import { isTaskOverdue, isTaskDueToday } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  ClipboardList,
  Calendar,
  AlertCircle,
  Clock,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const filters = ['All', 'Today', 'This Week', 'Overdue', 'High Priority'];

const priorityConfig = {
  high: {
    label: 'High',
    className: 'border-red-500 text-red-500',
  },
  medium: {
    label: 'Medium',
    className: 'border-muted-foreground text-muted-foreground',
  },
  low: {
    label: 'Low',
    className: 'border-green-600 text-green-600',
  },
};

const statusConfig = {
  'not-started': {
    label: 'Not Started',
    icon: Circle,
    className: 'text-muted-foreground',
  },
  'in-progress': {
    label: 'In Progress',
    icon: Clock,
    className: 'text-blue-600 dark:text-blue-400',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    className: 'text-green-600 dark:text-green-400',
  },
};

export function WorkloadOverview() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksData, clientsData] = await Promise.all([
        tasksService.getTasks(),
        clientsService.getClients(),
      ]);
      setTasks(tasksData.filter((t) => t.status !== 'completed'));
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTasks = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    switch (activeFilter) {
      case 'All':
        return tasks;
      case 'Today':
        return tasks.filter((task) => isTaskDueToday(task));
      case 'This Week':
        return tasks.filter((task) => {
          if (!task.deadline) return false;
          const deadline = new Date(task.deadline);
          return deadline >= today && deadline <= weekFromNow;
        });
      case 'Overdue':
        return tasks.filter((task) => isTaskOverdue(task));
      case 'High Priority':
        return tasks.filter((task) => task.priority === 'high');
      default:
        return tasks;
    }
  };

  const filteredTasks = getFilteredTasks();

  const getClientName = (clientId?: string) => {
    if (!clientId) return '—';
    const client = clients.find((c) => c.id === clientId);
    return client?.name || '—';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-foreground">Tasks Overview</h1>
        <Button asChild>
          <Link to="/tasks/new">
            <Plus className="w-4 h-4 mr-2" />
            Add Task
          </Link>
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 border border-border rounded-lg p-1 mb-6 w-fit">
        {filters.map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
              activeFilter === filter
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
            )}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="border border-border rounded-lg p-4">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks Table */}
      {!loading && (
        <>
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12 border border-border rounded-lg">
              <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No tasks for this filter</p>
              <Button asChild>
                <Link to="/tasks/new">
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first task
                </Link>
              </Button>
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Deadline</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map((task) => {
                    const isOverdue = isTaskOverdue(task);
                    const statusConf = statusConfig[task.status];
                    const priorityConf = priorityConfig[task.priority];
                    const StatusIcon = statusConf.icon;

                    return (
                      <TableRow
                        key={task.id}
                        onClick={() => navigate(`/tasks/${task.id}`)}
                        className="cursor-pointer"
                      >
                        <TableCell>
                          <div className="font-medium text-foreground">{task.title}</div>
                          {task.description && (
                            <div className="text-sm text-muted-foreground mt-1 line-clamp-1">
                              {task.description}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-foreground">
                          {getClientName(task.clientId)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <StatusIcon className={cn('h-4 w-4', statusConf.className)} />
                            <span className={cn('text-sm', statusConf.className)}>
                              {statusConf.label}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={priorityConf.className}>
                            {priorityConf.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {task.deadline ? (
                            <div
                              className={cn(
                                'flex items-center gap-1.5 text-foreground',
                                isOverdue && 'text-destructive font-medium'
                              )}
                            >
                              {isOverdue && <AlertCircle className="h-4 w-4" />}
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span>
                                {new Date(task.deadline).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
